<template>
  <div class="ue-expenses-edit">
    <el-card>
      <div class="header">
        <h2>149-2.1.3.4 Материалы (Год: {{ dto.trancheYear }})</h2>
        <div>
          <el-button @click="goBack">Назад</el-button>
          <el-button type="primary" @click="submitForm()" :loading="isLoading"
                     v-if="dto.status !== 'APPROVED' && hasEditPermission()">Сохранить</el-button>
        </div>
      </div>

      <el-alert
          v-if="noContingent"
          type="warning"
          :closable="false"
          show-icon
          class="mb-2"
          title="Контингент за этот год не заполнен — расчёт по специальностям = 0"
      />

      <div class="table-container" @paste="handlePaste" @keydown="handleKeydown">
        <!-- ===== Таблица 1: вспомогательная строка ===== -->
        <table class="grid-table mb-3">
          <thead>
          <tr>
            <th class="aux-name-cell header-success">Вспомогательная строка</th>
            <th class="total-cell header-success">Общая сумма</th>
            <th v-for="name in monthNames" :key="'auxh-'+name" class="month-cell header-success">{{ name }}</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td class="aux-name-cell text-left">Расходные учебные материалы (на практические занятия, расходные
              материалы для преподавателей и студентов, практических работ и пр.)</td>
            <td class="calculated-cell">{{ formatNumber(auxTotal) }}</td>
            <td v-for="(field, colIdx) in auxFields" :key="field" class="expense-sum-edit">
              <input
                  v-if="editable"
                  :value="getCellValue(dto, field)"
                  @focus="onFocus($event, dto, field)"
                  @blur="onBlur($event, dto, field)"
                  @input="onInput($event, dto, field)"
                  @keypress="onKeypress"
                  :data-row="0"
                  :data-col="colIdx"
                  class="cell-input"
                  type="text"
              />
              <span v-else>{{ formatNumber(dto[field]) }}</span>
            </td>
          </tr>
          </tbody>
        </table>

        <!-- ===== Таблица 2: основная ===== -->
        <table class="grid-table">
          <thead>
          <tr>
            <th class="group-name-cell header-success" rowspan="2">Наименование группы товара</th>
            <th class="title-cell header-success" rowspan="2">Код и наименование специальности</th>
            <th class="total-cell header-success" rowspan="2">Общая сумма</th>
            <th class="header-success" colspan="12">в т.ч. по месяцам</th>
          </tr>
          <tr>
            <th v-for="name in monthNames" :key="'h2-'+name" class="month-cell header-success">{{ name }}</th>
          </tr>
          </thead>
          <tbody>
          <!-- ВСЕГО -->
          <tr class="total-row calculated-row">
            <td colspan="2">ВСЕГО:</td>
            <td>{{ formatNumber(grandTotals.total) }}</td>
            <td v-for="(v, idx) in grandTotals.months" :key="'gt-'+idx">{{ formatNumber(v) }}</td>
          </tr>

          <!-- Группа: Расходные учебные материалы -->
          <tr class="product-group-row">
            <td class="fw-bold">{{ PG_EDU }}</td>
            <td></td>
            <td>{{ formatNumber(eduTotals.total) }}</td>
            <td v-for="(v, idx) in eduTotals.months" :key="'et-'+idx">{{ formatNumber(v) }}</td>
          </tr>
          <tr v-for="row in eduRows" :key="row.item.uuid || row.item.eduAreaId" class="readonly-text">
            <td></td>
            <td class="text-left">{{ row.label }}</td>
            <td class="calculated-cell">{{ formatNumber(row.total) }}</td>
            <td v-for="(v, idx) in row.months" :key="'er-'+idx" class="calculated-cell">{{ formatNumber(v) }}</td>
          </tr>

          <!-- Группа: Административные расходы -->
          <tr class="product-group-row">
            <td class="fw-bold">{{ PG_ADMIN }}</td>
            <td></td>
            <td>{{ formatNumber(adminTotals.total) }}</td>
            <td v-for="(v, idx) in adminTotals.months" :key="'at-'+idx">{{ formatNumber(v) }}</td>
          </tr>
          <tr v-for="(row, rowIdx) in adminRows" :key="row.item.uuid || ('admin-'+rowIdx)">
            <td class="text-left admin-name-cell">{{ row.item.productName }}</td>
            <td></td>
            <td class="calculated-cell">{{ formatNumber(row.total) }}</td>
            <td v-for="(field, colIdx) in monthFields" :key="field" class="expense-sum-edit">
              <input
                  v-if="editable"
                  :value="getCellValue(row.item, field)"
                  @focus="onFocus($event, row.item, field)"
                  @blur="onBlur($event, row.item, field)"
                  @input="onInput($event, row.item, field)"
                  @keypress="onKeypress"
                  :data-row="rowIdx + 1"
                  :data-col="colIdx"
                  class="cell-input"
                  type="text"
              />
              <span v-else>{{ formatNumber(row.item[field]) }}</span>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </el-card>
  </div>

  <vue-loading :active.sync="isLoading" :is-full-page="true"></vue-loading>
</template>

<script>
import VueLoading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import {showErrorNotify, showSuccessNotify} from "@/utils/notificationUtils";
import ueExpensesCommonCrudService from "@/services/expenses/ueExpensesCommonCrudService";
import delegationService from "@/services/delegationService";
import {handleGridKeydown} from "@/utils/gridKeyboardNav";

const MONTH_FIELDS = ['month1','month2','month3','month4','month5','month6','month7','month8','month9','month10','month11','month12'];
const AUX_FIELDS = ['auxMonth1','auxMonth2','auxMonth3','auxMonth4','auxMonth5','auxMonth6','auxMonth7','auxMonth8','auxMonth9','auxMonth10','auxMonth11','auxMonth12'];

const PG_EDU = 'Расходные учебные материалы';
const PG_ADMIN = 'Административные расходы';

export default {
  components: { VueLoading },
  data() {
    return {
      isLoading: false,
      expenseType: 'materials',
      PG_EDU,
      PG_ADMIN,
      dto: {
        uuid: null,
        trancheYear: null,
        universityId: null,
        status: null,
        items: []
      },
      shares: {},
      noContingent: false,
      monthFields: MONTH_FIELDS,
      auxFields: AUX_FIELDS,
      monthNames: [
        'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
        'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'
      ],
      focusedKey: null,
      editingValue: '',
    };
  },
  computed: {
    uuid() {
      return this.$route.params.uuid;
    },
    editable() {
      return this.dto.status !== 'APPROVED' && this.hasEditPermission();
    },
    auxValues() {
      return AUX_FIELDS.map(f => this.parseNumber(this.dto[f]));
    },
    auxTotal() {
      return this.auxValues.reduce((a, b) => a + b, 0);
    },
    eduRows() {
      const items = (this.dto.items || []).filter(i => i.productGroup === PG_EDU);
      return items.map(item => {
        const disc = (item.eduAreaCode || '').slice(-2);
        const share = this.shares[disc] != null ? Number(this.shares[disc]) : 0;
        const months = this.auxValues.map(v => this.roundTo2(v * share));
        const total = this.roundTo2(months.reduce((a, b) => a + b, 0));
        const label = item.eduAreaCode
            ? item.eduAreaCode + ' ' + (item.eduAreaName || '')
            : (item.eduAreaName || ('Специальность #' + item.eduAreaId));
        return { item, label, months, total };
      });
    },
    adminRows() {
      const items = (this.dto.items || []).filter(i => i.productGroup === PG_ADMIN);
      return items.map(item => {
        const months = MONTH_FIELDS.map(f => this.parseNumber(item[f]));
        const total = this.roundTo2(months.reduce((a, b) => a + b, 0));
        return { item, months, total };
      });
    },
    eduTotals() {
      return this.sumRows(this.eduRows);
    },
    adminTotals() {
      return this.sumRows(this.adminRows);
    },
    grandTotals() {
      const months = this.eduTotals.months.map((v, i) => this.roundTo2(v + this.adminTotals.months[i]));
      return { months, total: this.roundTo2(this.eduTotals.total + this.adminTotals.total) };
    },
    // editable month rows for paste/keyboard nav: index 0 = aux row, 1..N = admin rows
    editableMonthRows() {
      return [{ kind: 'aux' }, ...this.adminRows.map(r => ({ kind: 'admin', item: r.item }))];
    }
  },
  methods: {
    async init() {
      await this.findById(this.uuid);
    },

    async findById(uuid) {
      this.isLoading = true;
      try {
        this.dto = await ueExpensesCommonCrudService.findById(this.expenseType, uuid);
        if (!this.dto.items) this.dto.items = [];
        await this.loadShares();
      } catch (e) {
        showErrorNotify('Ошибка при загрузке данных');
      } finally {
        this.isLoading = false;
      }
    },

    async loadShares() {
      this.shares = {};
      this.noContingent = false;
      if (!this.dto.trancheYear || !this.dto.universityId) return;
      try {
        const res = await ueExpensesCommonCrudService.getMaterialsContingentShares(
            this.dto.trancheYear, this.dto.universityId
        );
        this.shares = res || {};
        this.noContingent = Object.keys(this.shares).length === 0;
      } catch (e) {
        this.shares = {};
        this.noContingent = true;
      }
    },

    async submitForm() {
      try {
        if (!this.editable) return;
        this.isLoading = true;
        // sync computed education-area months into the dto snapshot (backend recomputes too)
        this.syncEduMonths();
        this.dto = await ueExpensesCommonCrudService.save(this.expenseType, this.dto);
        if (!this.dto.items) this.dto.items = [];
        await this.loadShares();
        showSuccessNotify("Успешно сохранено!");
      } catch (exc) {
        showErrorNotify("Возникла ошибка при сохранении");
      } finally {
        this.isLoading = false;
      }
    },

    syncEduMonths() {
      this.eduRows.forEach(row => {
        MONTH_FIELDS.forEach((f, i) => {
          row.item[f] = row.months[i] === 0 ? null : row.months[i];
        });
        row.item.totalActual = row.total === 0 ? null : row.total;
      });
      this.adminRows.forEach(row => {
        row.item.totalActual = row.total === 0 ? null : row.total;
      });
    },

    sumRows(rows) {
      const months = new Array(12).fill(0);
      rows.forEach(r => r.months.forEach((v, i) => months[i] += v));
      const rounded = months.map(v => this.roundTo2(v));
      return { months: rounded, total: this.roundTo2(rounded.reduce((a, b) => a + b, 0)) };
    },

    parseNumber(str) {
      if (str === null || str === undefined || str === '') return 0;
      const cleaned = String(str).replace(/\s/g, '').replace(',', '.');
      const num = parseFloat(cleaned);
      return isNaN(num) ? 0 : num;
    },

    formatNumber(val) {
      const num = typeof val === 'number' ? val : this.parseNumber(val);
      if (num === 0) return '';
      return num.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    },

    cellKey(item, field) {
      return (item && item.uuid ? item.uuid : 'root') + ':' + field;
    },

    getCellValue(item, field) {
      if (this.focusedKey && this.focusedKey === this.cellKey(item, field)) {
        return this.editingValue;
      }
      return this.getDisplayValue(item[field]);
    },

    getDisplayValue(val) {
      const num = this.parseNumber(val);
      if (num === 0) return '';
      return num.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    },

    onFocus(event, item, field) {
      this.focusedKey = this.cellKey(item, field);
      const num = this.parseNumber(item[field]);
      this.editingValue = num === 0 ? '' : String(num).replace('.', ',');
      event.target.value = this.editingValue;
    },

    onBlur(event, item, field) {
      this.focusedKey = null;
      const num = this.parseNumber(event.target.value);
      item[field] = num === 0 ? null : this.roundTo2(num);
      event.target.value = this.getDisplayValue(item[field]);
    },

    onInput(event, item, field) {
      // treat a typed dot as the decimal comma
      let sanitized = event.target.value.replace(/\./g, ',').replace(/[^\d,]/g, '');
      const parts = sanitized.split(',');
      if (parts.length > 2) {
        sanitized = parts[0] + ',' + parts.slice(1).join('');
      }
      if (sanitized !== event.target.value) {
        const pos = event.target.selectionStart;
        event.target.value = sanitized;
        if (pos != null) event.target.setSelectionRange(pos, pos);
      }
      this.editingValue = sanitized;
      const num = this.parseNumber(sanitized);
      item[field] = num === 0 ? null : this.roundTo2(num);
    },

    onKeypress(event) {
      const char = event.key;
      if (char.length > 1) return;
      if (/[0-9]/.test(char)) return;
      // accept both "." and "," as the decimal separator (dot becomes comma in onInput)
      if ((char === ',' || char === '.') && !event.target.value.includes(',')) return;
      event.preventDefault();
    },

    roundTo2(num) {
      return Math.round(num * 100) / 100;
    },

    setMonthCell(row, colIdx, num) {
      if (!row || colIdx < 0 || colIdx >= 12) return;
      const val = num === 0 ? null : this.roundTo2(num);
      if (row.kind === 'aux') {
        this.dto[AUX_FIELDS[colIdx]] = val;
      } else if (row.kind === 'admin' && row.item) {
        row.item[MONTH_FIELDS[colIdx]] = val;
      }
    },

    handleKeydown(event) {
      handleGridKeydown(event, this.$el);
    },

    handlePaste(event) {
      const target = event.target;
      if (!target || target.tagName !== 'INPUT') return;

      const startRow = parseInt(target.dataset.row);
      const startCol = parseInt(target.dataset.col);
      if (isNaN(startRow) || isNaN(startCol)) return;

      const clipboardData = event.clipboardData || window.clipboardData;
      if (!clipboardData) return;

      const text = clipboardData.getData('text/plain');
      if (!text) return;

      const rows = text.split(/\r?\n/).filter(r => r.trim() !== '');
      if (rows.length <= 1 && !rows[0].includes('\t')) return;

      event.preventDefault();

      const editableRows = this.editableMonthRows;
      for (let r = 0; r < rows.length; r++) {
        const editableRow = editableRows[startRow + r];
        if (!editableRow) break;
        const cols = rows[r].split('\t');
        for (let c = 0; c < cols.length; c++) {
          this.setMonthCell(editableRow, startCol + c, this.parseNumber(cols[c]));
        }
      }
    },

    goBack() {
      this.$router.go(-1);
    },
    hasEditPermission() {
      return delegationService.isUniversityManager() && (this.dto.status === 'CREATED' || !this.dto.status);
    },
  },
  mounted() {
    this.init();
  }
};
</script>

<style scoped>
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.table-container {
  overflow-x: auto;
}

.grid-table {
  border-collapse: collapse;
  width: 100%;
  font-size: 13px;
}

.grid-table th,
.grid-table td {
  border: 1px solid #dcdfe6;
  padding: 4px 6px;
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
}

.grid-table th {
  background-color: #f0f9eb;
  font-weight: 600;
  font-size: 12px;
}

.header-success {
  background-color: #f0f9eb !important;
}

.aux-name-cell { min-width: 360px; white-space: normal; }
.group-name-cell { min-width: 220px; }
.title-cell { min-width: 320px; }
.admin-name-cell { padding-left: 24px !important; }
.total-cell { min-width: 120px; }
.month-cell { min-width: 90px; }

.text-left { text-align: left !important; }
.fw-bold { font-weight: 700; }
.mb-2 { margin-bottom: 8px; }
.mb-3 { margin-bottom: 16px; }

.total-row {
  background-color: #f0f9eb;
  font-weight: bold;
}

.product-group-row {
  background-color: #fef0f0;
  font-weight: 700;
}

.calculated-cell {
  background-color: #fafafa;
  color: #606266;
}

.readonly-text td {
  color: #303133;
}

.expense-sum-edit {
  padding: 0 !important;
}

.cell-input {
  width: 100%;
  border: none;
  outline: none;
  text-align: center;
  padding: 4px 6px;
  font-size: 13px;
  background: transparent;
  box-sizing: border-box;
}

.cell-input:focus {
  background-color: #fff8e6;
}
</style>
